import { IconButton } from '@mui/material'
import { MaterialReactTable } from 'material-react-table'
import { useEffect, useRef, useState } from 'react'
import { MdConnectWithoutContact, MdRemoveRedEye, MdCheckCircle, MdCancel, MdVisibility } from "react-icons/md"
import { RiDeleteBin2Line } from 'react-icons/ri'
import { useModifiedMaterialReactTable } from '../misc/modified_react_table'
import { useAllClientSummariesLookup } from '../processes/all_client_summaries_lookup'
import { useAllUserSummariesLookup } from '../processes/all_user_summaries_lookup'
import { useUserClientAssignment, useUserClientAssignmentState } from '../processes/user_client_assignment'
import { useUserOtpReveal } from '../processes/user_otp_reveal'

function formatDate(dateString) {
  if (!dateString) return '-'
  return new Date(dateString).toLocaleString('id-ID', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

function Modal({ isOpen, onClose, title, subtitle, children, size = "tw-max-w-4xl" }) {
  const dialogRef = useRef(null)

  useEffect(() => {
    if (isOpen && dialogRef.current) {
      dialogRef.current.showModal()
    } else if (!isOpen && dialogRef.current) {
      dialogRef.current.close()
    }
  }, [isOpen])

  if (!isOpen) return null

  return (
    <dialog
      ref={dialogRef}
      className='tw-modal tw-modal-bottom sm:tw-modal-middle'
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.stopPropagation()
        }
      }}
      onClose={onClose}
    >
      <div className={`tw-modal-box tw-border tw-border-[revert] tw-p-0 tw-flex tw-flex-col ${size}`}>
        <div
          className={[
            'tw-font-semibold',
            'tw-bg-primary',
            'tw-text-primary-content',
            'tw-p-4',
            'sm:tw-p-5',
            'tw-flex',
            'tw-items-center',
          ].join(' ')}
        >
          <div>
            <h3 className="tw-font-bold tw-text-lg">{title}</h3>
            {subtitle && <p className="tw-text-sm tw-opacity-80">{subtitle}</p>}
          </div>
        </div>
        <div className={[
          'tw-px-4',
          'sm:tw-px-5',
          'tw-pb-4',
          'sm:tw-pb-5',
          'tw-overflow-y-auto',
          'tw-space-y-4',
          'tw-pt-4'
        ].join(' ')}>
          {children}
        </div>
      </div>
      <form method='dialog' className='tw-modal-backdrop'>
        <button>close</button>
      </form>
    </dialog>
  )
}

function DetailTable({ data, fieldConfig }) {
  const renderCellValue = (value, config, rowData) => {
    if (config.type === 'email' && config.verificationKey) {
      const isVerified = rowData[config.verificationKey] === 1
      return (
        <div className="tw-flex tw-items-center tw-gap-2">
          <span className="tw-text-gray-900">{value || '-'}</span>
          {isVerified ? (
            <div className="tw-flex tw-items-center tw-gap-1">
              <MdCheckCircle className="tw-text-green-500" style={{ fontSize: 18 }} title="Verified" />
              <span className="tw-text-sm tw-text-green-600">Verified</span>
            </div>
          ) : (
            <div className="tw-flex tw-items-center tw-gap-1">
              <MdCancel className="tw-text-red-500" style={{ fontSize: 18 }} title="Not Verified" />
              <span className="tw-text-sm tw-text-red-600">Not Verified</span>
            </div>
          )}
        </div>
      )
    }

    if (config.type === 'date') {
      return <span className="tw-text-gray-900">{formatDate(value)}</span>
    }

    if (config.accessor && config.accessor.includes('.')) {
      const keys = config.accessor.split('.')
      let nestedValue = rowData
      for (const key of keys) {
        nestedValue = nestedValue?.[key]
      }
      return <span className="tw-text-gray-900">{nestedValue || '-'}</span>
    }

    if (config.render && typeof config.render === 'function') {
      return config.render(value, rowData)
    }
    return <span className="tw-text-gray-900">{value || '-'}</span>
  }

  if (!data) return null

  return (
    <div className="tw-overflow-auto">
      <table className="tw-table-auto tw-w-full tw-mb-4 tw-border tw-border-gray-300">
        <tbody>
          {fieldConfig.map((field, index) => {
            const value = field.accessor ?
              (field.accessor.includes('.') ? null : data[field.accessor]) :
              data[field.key]

            return (
              <tr key={index} className="hover:tw-bg-gray-50">
                <td className="tw-px-4 tw-py-3 tw-text-sm tw-border tw-border-gray-300">
                  <span className="tw-font-semibold tw-text-gray-700">{field.label}</span>
                </td>
                <td className="tw-px-4 tw-py-3 tw-text-sm tw-border tw-border-gray-300">
                  {renderCellValue(value, field, data)}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function DetailDialog({ isOpen, onClose, title, subtitle, data, fieldConfig, actionButtons }) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
    >
      <DetailTable data={data} fieldConfig={fieldConfig} />

      {actionButtons && (
        <div className="tw-flex tw-items-center tw-pt-4 tw-mt-4 tw-border-t tw-justify-end tw-gap-2">
          {actionButtons}
        </div>
      )}
    </Modal>
  )
}

// Reveal-on-click cell for the masked OTP column. Calls the audited
// /user-otp-reveal endpoint, displays the real OTP for 10 seconds, then
// auto-rolls back to the masked value. Each reveal generates an
// OTP_REVEALED audit event server-side (A.8.11 + A.8.16).
function OtpCell({ userId, masked }) {
  const [revealed, setRevealed] = useState(null)
  const [error, setError] = useState(null)
  const { mutateAsync, isPending } = useUserOtpReveal()

  useEffect(() => {
    if (!revealed) return
    const t = setTimeout(() => setRevealed(null), 10000)
    return () => clearTimeout(t)
  }, [revealed])

  useEffect(() => {
    if (!error) return
    const t = setTimeout(() => setError(null), 3000)
    return () => clearTimeout(t)
  }, [error])

  if (revealed) {
    return (
      <span
        className="tw-font-mono tw-text-blue-600"
        title="Auto-hides in 10 seconds"
      >
        {revealed}
      </span>
    )
  }

  return (
    <div className="tw-flex tw-items-center tw-gap-1">
      <span className="tw-font-mono">{masked}</span>
      <IconButton
        size="small"
        title="Reveal OTP (audited)"
        disabled={isPending}
        onClick={async () => {
          setError(null)
          try {
            const result = await mutateAsync({ userId })
            setRevealed(result?.otp ?? '(empty)')
          } catch (err) {
            setError(err?.message || 'failed')
          }
        }}
      >
        <MdVisibility fontSize={'1.2rem'} />
      </IconButton>
      {error && (
        <span className="tw-text-xs tw-text-red-500">{error}</span>
      )}
    </div>
  )
}

export default function Users() {
  const [
    userSummaryForClientAssignment,
    setUserSummaryForClientAssignment,
  ] = useState()

  const [userDetailForView, setUserDetailForView] = useState()

  const {
    isPending: userClientAssignmentIsInProgress,
  } = useUserClientAssignmentState()

  const {
    data: allUserSummaries,
    isLoading: isLoadingAllUserSummaries,
    isFetching: isFetchingAllUserSummaries,
  } = useAllUserSummariesLookup()

  const allUserSummariesTable = useModifiedMaterialReactTable({
    data: allUserSummaries,
    columns: [
      {
        id: 'id',
        header: 'Id',
        accessorKey: 'uuid',
        Cell: ({ row, cell }) => {
          return <span>
            ...{cell.getValue().slice(-5)}
          </span>
        },
      },
      {
        id: 'fullName',
        header: 'Full Name',
        Cell: ({ row }) => {
          const name = row.original.name;
          const lastName = row.original.lastName;
          return <span>{`${name} ${lastName}`}</span>
        },
      },
      {
        id: 'emailOtp',
        header: 'OTP',
        accessorKey: 'emailOtp',
        Cell: ({ row, cell }) => {
          const masked = cell.getValue()
          if (!masked || masked.length >= 10) return <span>-</span>
          return <OtpCell userId={row.original.userId} masked={masked} />
        },
      },
      {
        id: 'company',
        header: 'Company',
        accessorKey: 'client.clientName',
        Cell: ({ row, cell }) => {
          if (cell.getValue()) {
            return <span>{`${cell.getValue()}`}</span>
          } else {
            return <span>-</span>
          }
        },
      },
      {
        id: 'email',
        header: 'Email',
        accessorKey: 'email',
        Cell: ({ row, cell }) => {
          const isVerified = row.original.emailVerified === 1;
          return (
            <div className="tw-flex tw-items-center tw-gap-2">
              <span>{cell.getValue()}</span>
              {isVerified ? (
                <MdCheckCircle className="tw-text-green-500 tw-text-lg" title="Email Verified" />
              ) : (
                <MdCancel className="tw-text-red-500 tw-text-lg" title="Email Not Verified" />
              )}
            </div>
          );
        },
      },
      {
        id: 'phonenumber',
        header: 'Phone Number',
        accessorKey: 'phoneNumber',
      },
      {
        id: 'role',
        header: 'Role',
        accessorKey: 'role',
      },
    ],
    getRowId: (row) => row.userUuid,
    enablePagination: true,
    state: {
      isLoading: isLoadingAllUserSummaries,
      showProgressBars: isFetchingAllUserSummaries ||
        userClientAssignmentIsInProgress,
      isSaving: userClientAssignmentIsInProgress,
    },
    enableRowActions: true,
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: 'Actions',
      },
    },
    positionActionsColumn: 'last',
    renderRowActions: ({ table, row }) => {
      return [
        <IconButton
          key={`view-${row?.id}`}
          title="View Details"
          className=""
          onClick={() => {
            setUserDetailForView(row.original)
          }}
        >
          <MdRemoveRedEye fontSize={'2rem'} />
        </IconButton>,
        <IconButton
          key={`assign-${row?.id}`}
          title="Client Assignment"
          className=""
          onClick={() => {
            setUserSummaryForClientAssignment(row.original)
          }}
        >
          <MdConnectWithoutContact fontSize={'2rem'} />
        </IconButton>
      ]
    },
  })

  return <div
    className={[
      'tw-flex-1',
      'tw-flex',
      'tw-flex-col',
      'tw-items-stretch',
      'tw-p-7',
    ].join(' ')}
  >
    <div className=''>
      <MaterialReactTable
        table={allUserSummariesTable}
      />
    </div>
    <UserDetailDialog
      userDetail={userDetailForView}
      onClose={() => setUserDetailForView()}
    />
    <UserClientAssignmentDialog
      userSummary={userSummaryForClientAssignment}
      onClose={() => setUserSummaryForClientAssignment()}
    />
  </div>
}
function UserDetailDialog({ userDetail, onClose }) {
  const userDetailDialog = useRef()

  useEffect(() => {
    if (userDetail) {
      userDetailDialog.current.showModal()
    } else {
      userDetailDialog.current.close()
    }
  }, [userDetail])

  return (
    <dialog
      ref={userDetailDialog}
      className='tw-modal tw-modal-bottom sm:tw-modal-middle'
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.stopPropagation()
        }
      }}
      onClose={onClose}
    >
      <div className='tw-modal-box tw-border tw-border-[revert] tw-p-0 tw-flex tw-flex-col tw-max-w-4xl'>
        <div
          className={[
            'tw-font-semibold',
            'tw-bg-primary',
            'tw-text-primary-content',
            'tw-p-4',
            'sm:tw-p-5',
            'tw-flex',
            'tw-items-center',
          ].join(' ')}
        >
          User Details
        </div>
        <div className={[
          'tw-px-4',
          'sm:tw-px-5',
          'tw-pb-4',
          'sm:tw-pb-5',
          'tw-overflow-y-auto',
          'tw-space-y-4',
          'tw-pt-4'
        ].join(' ')}>
          <div className="tw-overflow-auto">
            <table className="tw-table-auto tw-w-full tw-mb-4 tw-border tw-border-gray-300">
              <tbody>
                <tr className="hover:tw-bg-gray-50">
                  <td className="tw-px-4 tw-py-3 tw-text-sm tw-border tw-border-gray-300">
                    <span className="tw-font-semibold tw-text-gray-700">UUID</span>
                  </td>
                  <td className="tw-px-4 tw-py-3 tw-text-sm tw-border tw-border-gray-300">
                    <span className="tw-text-gray-900">{userDetail?.uuid || '-'}</span>
                  </td>
                </tr>
                <tr className="hover:tw-bg-gray-50">
                  <td className="tw-px-4 tw-py-3 tw-text-sm tw-border tw-border-gray-300">
                    <span className="tw-font-semibold tw-text-gray-700">First Name</span>
                  </td>
                  <td className="tw-px-4 tw-py-3 tw-text-sm tw-border tw-border-gray-300">
                    <span className="tw-text-gray-900">{userDetail?.name || '-'}</span>
                  </td>
                </tr>
                <tr className="hover:tw-bg-gray-50">
                  <td className="tw-px-4 tw-py-3 tw-text-sm tw-border tw-border-gray-300">
                    <span className="tw-font-semibold tw-text-gray-700">Last Name</span>
                  </td>
                  <td className="tw-px-4 tw-py-3 tw-text-sm tw-border tw-border-gray-300">
                    <span className="tw-text-gray-900">{userDetail?.lastName || '-'}</span>
                  </td>
                </tr>
                <tr className="hover:tw-bg-gray-50">
                  <td className="tw-px-4 tw-py-3 tw-text-sm tw-border tw-border-gray-300">
                    <span className="tw-font-semibold tw-text-gray-700">Email</span>
                  </td>
                  <td className="tw-px-4 tw-py-3 tw-text-sm tw-border tw-border-gray-300">
                    <div className="tw-flex tw-items-center tw-gap-2">
                      <span className="tw-text-gray-900">{userDetail?.email || '-'}</span>
                      {userDetail?.emailVerified === 1 ? (
                        <div className="tw-flex tw-items-center tw-gap-1">
                          <MdCheckCircle className="tw-text-green-500" style={{ fontSize: 18 }} title="Verified" />
                          <span className="tw-text-sm tw-text-green-600">Verified</span>
                        </div>
                      ) : (
                        <div className="tw-flex tw-items-center tw-gap-1">
                          <MdCancel className="tw-text-red-500" style={{ fontSize: 18 }} title="Not Verified" />
                          <span className="tw-text-sm tw-text-red-600">Not Verified</span>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
                <tr className="hover:tw-bg-gray-50">
                  <td className="tw-px-4 tw-py-3 tw-text-sm tw-border tw-border-gray-300">
                    <span className="tw-font-semibold tw-text-gray-700">Phone Number</span>
                  </td>
                  <td className="tw-px-4 tw-py-3 tw-text-sm tw-border tw-border-gray-300">
                    <span className="tw-text-gray-900">{userDetail?.phoneNumber || '-'}</span>
                  </td>
                </tr>
                <tr className="hover:tw-bg-gray-50">
                  <td className="tw-px-4 tw-py-3 tw-text-sm tw-border tw-border-gray-300">
                    <span className="tw-font-semibold tw-text-gray-700">Role</span>
                  </td>
                  <td className="tw-px-4 tw-py-3 tw-text-sm tw-border tw-border-gray-300">
                    <span className="tw-text-gray-900">{userDetail?.role || '-'}</span>
                  </td>
                </tr>
                <tr className="hover:tw-bg-gray-50">
                  <td className="tw-px-4 tw-py-3 tw-text-sm tw-border tw-border-gray-300">
                    <span className="tw-font-semibold tw-text-gray-700">Department</span>
                  </td>
                  <td className="tw-px-4 tw-py-3 tw-text-sm tw-border tw-border-gray-300">
                    <span className="tw-text-gray-900">{userDetail?.department || '-'}</span>
                  </td>
                </tr>
                <tr className="hover:tw-bg-gray-50">
                  <td className="tw-px-4 tw-py-3 tw-text-sm tw-border tw-border-gray-300">
                    <span className="tw-font-semibold tw-text-gray-700">Client Name</span>
                  </td>
                  <td className="tw-px-4 tw-py-3 tw-text-sm tw-border tw-border-gray-300">
                    <span className="tw-text-gray-900">{userDetail?.client?.clientName || '-'}</span>
                  </td>
                </tr>
                <tr className="hover:tw-bg-gray-50">
                  <td className="tw-px-4 tw-py-3 tw-text-sm tw-border tw-border-gray-300">
                    <span className="tw-font-semibold tw-text-gray-700">Created At</span>
                  </td>
                  <td className="tw-px-4 tw-py-3 tw-text-sm tw-border tw-border-gray-300">
                    <span className="tw-text-gray-900">{formatDate(userDetail?.createdAt)}</span>
                  </td>
                </tr>
                <tr className="hover:tw-bg-gray-50">
                  <td className="tw-px-4 tw-py-3 tw-text-sm tw-border tw-border-gray-300">
                    <span className="tw-font-semibold tw-text-gray-700">Updated At</span>
                  </td>
                  <td className="tw-px-4 tw-py-3 tw-text-sm tw-border tw-border-gray-300">
                    <span className="tw-text-gray-900">{formatDate(userDetail?.updatedAt)}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="tw-flex tw-items-center tw-pt-4 tw-mt-4 tw-border-t tw-justify-end">
            <button
              className="tw-btn tw-btn-primary"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
      <form method='dialog' className='tw-modal-backdrop'>
        <button>close</button>
      </form>
    </dialog>
  )
}

function UserClientAssignmentDialog({
  userSummary,
  onClose,
}) {
  const {
    data: clientSummaries,
  } = useAllClientSummariesLookup()
  const [client, setClient] = useState(undefined)
  useEffect(() => {
    setClient(
      clientSummaries?.find(clientSummary => {
        return clientSummary.clientId === userSummary?.client.clientId
      }) ?? undefined
    )
  }, [userSummary, clientSummaries])
  const clientId = client?.clientId
  const clientAssignmentDialog = useRef()
  const {
    isSuccess: userClientAssignmentHasSucceeded,
    mutate: userClientAssignment,
  } = useUserClientAssignment()
  useEffect(() => {
    if (userClientAssignmentHasSucceeded) {
      clientAssignmentDialog.current.close()
    }
  }, [userClientAssignmentHasSucceeded])
  useEffect(() => {
    if (userSummary) {
      clientAssignmentDialog.current.showModal()
    } else {
      clientAssignmentDialog.current.close()
    }
  }, [userSummary])

  return <dialog
    ref={clientAssignmentDialog}
    className='tw-modal tw-modal-bottom sm:tw-modal-middle'
    onKeyDown={(event) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
      }
    }}
    onClose={onClose}
  >
    <div className='tw-modal-box tw-border tw-border-[revert] tw-p-0 tw-flex tw-flex-col'>
      <div
        className={[
          'tw-font-semibold',
          'tw-bg-primary',
          'tw-text-primary-content',
          'tw-p-4',
          'sm:tw-p-5',
          'tw-flex',
          'tw-items-center',
        ].join(' ')}
      >
        User Client Assignment
      </div>
      <div id='assigned-emissions' className={[
        'tw-px-4',
        'sm:tw-px-5',
        'tw-pb-4',
        'sm:tw-pb-5',
        'tw-overflow-y-auto',
      ].join(' ')}>
        <div className='tw-form-control tw-w-full'>
          <label className='tw-label'>
            <span className='tw-label-text'>
              Name
            </span>
          </label>
          <input
            type='text'
            placeholder='/ Type Here'
            className={[
              'tw-input',
              'tw-input-bordered',
              'tw-input-primary',
              'tw-w-full',
            ].join(' ')}
            defaultValue={userSummary?.name}
            disabled
          />
        </div>
        <div className='tw-form-control tw-w-full'>
          <div className='tw-label'>
            <span className='tw-label-text'>
              Client
            </span>
          </div>
          <div className='tw-flex tw-flex-col tw-gap-3'>
            <div className='tw-flex'>
              <div className='tw-flex-1 tw-flex tw-flex-col'>
                <FilterableSelect
                  className='tw-justify-start tw-border-l-4'
                  placeholder={'/ Client'}
                  idKey={'clientId'}
                  nameKey={'clientName'}
                  values={clientSummaries}
                  value={client}
                  onChange={(newClient) => setClient(
                    newClient
                  )}
                />
              </div>
              <button className={[
                'tw-flex',
                'tw-justify-center',
                'tw-items-center',
                'tw-p-1',
                'tw-border',
                'tw-border-[revert]',
                'tw-border-l-0',
              ].join(' ')}>
                <RiDeleteBin2Line
                  className='tw-text-2xl'
                  onClick={() => {
                    setClient()
                  }}
                />
              </button>
            </div>
          </div>
        </div>
        <div className='tw-pt-4'>
          <button
            type='button'
            className='tw-btn tw-btn-primary tw-w-full'
            onClick={() => {
              userClientAssignment({
                userId: userSummary.userId,
                clientId: clientId,
              })
            }}
          >
            Submit
          </button>
        </div>
      </div>
    </div>
    <form method='dialog' className='tw-modal-backdrop'>
      <button>close</button>
    </form>
  </dialog>
}

function FilterableSelect({
  className,
  idKey,
  nameKey,
  placeholder,
  values,
  value,
  valueIndex,
  onChange,
}) {
  const [search, setSearch] = useState('')
  const searchedValues = values?.filter(value => {
    const name = value[nameKey]
    return name.toLowerCase().includes(search.toLowerCase())
  })
  const dialog = useRef()
  const [show, setShow] = useState(false)
  useEffect(() => {
    show && dialog.current.showModal()
  }, [show])
  const [position, setPosition] = useState(0)
  useEffect(() => {
    setPosition(0)
  }, [search, show])
  const option = useRef()
  useEffect(() => {
    option.current?.scrollIntoView({
      behavior: 'instant',
      block: 'nearest',
    })
  }, [position])

  return <>
    <button
      onClick={() => setShow(true)}
      className={[
        'tw-btn',
        'tw-btn-outline',
        'tw-btn-primary',
        'tw-no-animation',
        !value ? 'tw-text-violet-400' : '',
        ...className?.split(' '),
      ].join(' ')}
    >
      {value?.[nameKey] ?? placeholder}
    </button>
    {show && <dialog
      ref={dialog}
      className='tw-modal tw-modal-bottom sm:tw-modal-middle'
      onKeyDown={(event) => {
        event.stopPropagation()
        if (
          event.key === 'ArrowDown' &&
          position + 1 < searchedValues?.length
        ) {
          setPosition(position + 1)
        } else if (
          event.key === 'ArrowUp' &&
          position - 1 > -1
        ) {
          setPosition(position - 1)
        } else if (
          event.key === 'Enter' &&
          searchedValues?.length
        ) {
          dialog.current.close()
          onChange(searchedValues[position], valueIndex)
        }
      }}
      onClose={(event) => {
        event.stopPropagation()
        setSearch('')
        setShow(false)
      }}
    >
      <div className={[
        'tw-modal-box',
        'tw-border',
        'tw-border-[revert]',
        'tw-p-0',
        'tw-flex',
        'tw-flex-col',
        'tw-h-full',
      ].join(' ')}>
        <div className={[
          'tw-px-4',
          'sm:tw-px-5',
          'tw-py-1',
          'sm:tw-py-2',
          'tw-bg-primary',
        ].join(' ')}>
          <input
            type='text'
            placeholder='/ Type Something'
            className={[
              'tw-input',
              'tw-bg-primary',
              'tw-text-primary-content',
              'tw-font-semibold',
              'placeholder:tw-text-primary-content',
              'placeholder:tw-font-semibold',
              'tw-w-full',
              'tw-p-0',
              'focus:tw-outline-none',
            ].join(' ')}
            value={search}
            onChange={(event) => {
              setSearch(event.target.value)
            }}
          />
        </div>
        <div
          id='dialog-body'
          className={[
            'tw-overflow-y-auto',
          ].join(' ')}
        >
          <div className='tw-flex-1 tw-flex tw-flex-col'>
            {searchedValues?.map((value, index) => {
              return <button
                ref={index === position ? option : null}
                className={[
                  'tw-flex-1',
                  'tw-btn',
                  'tw-btn-primary',
                  'tw-no-animation',
                  'tw-border-t-0',
                  'tw-border-x-0',
                  'tw-border-l-4',
                  'tw-justify-start',
                  'focus:tw-outline-none',
                  'tw-px-3',
                  'sm:tw-px-4',
                  index === position ? '' : 'tw-btn-outline',
                ].join(' ')}
                key={value[idKey]}
                onClick={() => {
                  dialog.current.close()
                  onChange(value, valueIndex)
                }}
              >
                {value[nameKey]}
              </button>
            })}
          </div>
        </div>
      </div>
      <form method='dialog' className='tw-modal-backdrop'>
        <button>close</button>
      </form>
    </dialog >}
  </>
}
